import React, { useState, useEffect } from 'react';
import { 
  FaArrowLeft, 
  FaCamera, 
  FaTimes, 
  FaSpinner, 
  FaUpload, 
  FaCheck, 
  FaStar,
  FaUserCircle,
  FaRegClock,
  FaQuoteRight,
  FaCertificate,
  FaGem,
  FaTrophy,
  FaRegLightbulb,
  FaThumbsUp,
  FaDollarSign,
  FaWallet,
  FaChartLine
} from 'react-icons/fa';
import { 
  IoWalletOutline, 
  IoImageOutline, 
  IoShareSocialOutline, 
  IoSparkles,
  IoTrendingUp,
  IoFlash
} from 'react-icons/io5';
import { MdAttachMoney, MdStars } from 'react-icons/md';
import BottomNavigation from './BottomNavigation';
import { useAuth } from '../security/security/AuthContext';
import axios from 'axios';


const SuccessStories = () => {
  const [currentView, setCurrentView] = useState('main');
  const [description, setDescription] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(false);
  const { userDetails, token, updateUserDetails } = useAuth();
  const [toastNotification, setToastNotification] = useState(null);

  const refreshUserData = async () => {
    try {
      setLoading(true);
      const userResponse = await axios.get(`https://api.globallequityfinance.com/api/v1/auth/${userDetails.email}`, {
        headers: {
          'Authorization': token
        }
      });
      updateUserDetails(userResponse.data);
      setLoading(false);
    } catch (error) {
      console.error('Error refreshing user data:', error);
      setToastNotification({ message: 'Failed to refresh user data', type: 'error' });
      setLoading(false);
    }
  };

  const fetchStories = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://api.globallequityfinance.com/api/proofs/approved');
      // Sort the proofs in descending order by ID before setting state
      const sortedStories = response.data.sort((a, b) => b.id - a.id);
      setStories(sortedStories);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching earnings stories:', error);
      setToastNotification({
        message: 'Failed to fetch earnings stories. Please try again.',
        type: 'error'
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    // Refresh user data when component mounts
    refreshUserData();
    fetchStories();
  }, []);

  useEffect(() => {
    if (currentView === 'main') {
      refreshUserData();
    }
  }, [currentView]);

  const handleImageSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedImage(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedImage) {
      setToastNotification({
        message: 'Please select a payout screenshot to upload',
        type: 'error'
      });
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('image', selectedImage);
    formData.append('description', description);
    formData.append('phoneNumber', phoneNumber);
    formData.append('userEmail', userDetails.email);

    try {
      await axios.post('https://api.globallequityfinance.com/api/proofs/add', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setToastNotification({
        message: 'Earnings proof submitted! It will be reviewed shortly.',
        type: 'success'
      });
      setCurrentView('main');
      setDescription('');
      setPhoneNumber('');
      setSelectedImage(null);
      fetchStories();
    } catch (error) {
      console.error('Error submitting story:', error);
      setToastNotification({
        message: 'Failed to submit your earnings proof. Please try again.',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Auto-dismiss toast functionality
  useEffect(() => {
    if (toastNotification) {
      const timer = setTimeout(() => {
        setToastNotification(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [toastNotification]);

  return (
    <div className="ge-earnings-container">
      {currentView === 'main' ? (
        <>
          <header className="ge-earnings-header">
            <div className="ge-header-glow"></div>
            <div className="ge-header-content">
              <div className="ge-title-section">
                <div className="ge-title-icon-wrapper">
                  <MdAttachMoney className="ge-title-icon" />
                  <IoSparkles className="ge-sparkle ge-sparkle-1" />
                  <IoSparkles className="ge-sparkle ge-sparkle-2" />
                </div>
                <div className="ge-title-text">
                  <h1 className="ge-main-title">Earnings Gallery</h1>
                  <p className="ge-subtitle">Real payouts from our investors</p>
                </div>
              </div>
            </div>
          </header>
          
          <div className="ge-earnings-content">
            {loading ? (
              <div className="ge-loading-state">
                <div className="ge-loading-circle">
                  <div className="ge-loading-inner"></div>
                </div>
                <h3 className="ge-loading-title">Loading Earnings</h3>
                <p className="ge-loading-text">Fetching success stories...</p>
              </div>
            ) : (
              <>
                {stories.length > 0 ? (
                  <div className="ge-earnings-grid">
                    {stories.map((story, index) => (
                      <div key={story.id} className="ge-earnings-card" style={{animationDelay: `${index * 0.1}s`}}>
                        <div className="ge-card-badge">
                          <FaCertificate className="ge-badge-icon" />
                          <span>Verified</span>
                        </div>
                        
                        <div className="ge-image-wrapper">
                          <img 
                            src={`https://api.globallequityfinance.com${story.imageUrl}`} 
                            alt="Earnings Proof" 
                            className="ge-earnings-image" 
                          />
                        </div>
                        
                        <div className="ge-card-content">
                          <div className="ge-story-section">
                            <div className="ge-quote-wrapper">
                              <FaQuoteRight className="ge-quote-icon" />
                            </div>
                            <p className="ge-story-description">{story.description}</p>
                          </div>
                          
                          <div className="ge-user-section">
                            <div className="ge-user-avatar">
                              <FaUserCircle className="ge-avatar-icon" />
                            </div>
                            <div className="ge-user-details">
                              <span className="ge-user-phone">
                                {story.phoneNumber.replace(/(\d{3})\d{4}(\d{3})/, '$1****$2')}
                              </span>
                              <div className="ge-date-wrapper">
                                <FaRegClock className="ge-clock-icon" />
                                <span className="ge-story-date">
                                  {new Date(story.createdAt).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="ge-empty-earnings">
                    <div className="ge-empty-animation">
                      <div className="ge-empty-circle">
                        <MdStars className="ge-empty-icon" />
                      </div>
                      <div className="ge-floating-icons">
                        <FaDollarSign className="ge-float-icon ge-float-1" />
                        <IoFlash className="ge-float-icon ge-float-2" />
                        <FaChartLine className="ge-float-icon ge-float-3" />
                      </div>
                    </div>
                    <h3 className="ge-empty-title">Be the First to Shine!</h3>
                    <p className="ge-empty-description">
                      Share your earnings proof and inspire the community with your success story.
                    </p>
                    <button 
                      className="ge-share-earnings-btn"
                      onClick={() => setCurrentView('share')}
                    >
                      <IoSparkles className="ge-btn-icon" />
                      <span>Share Your Earnings</span>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      ) : (
        <>
          <header className="ge-share-header">
            <button 
              className="ge-back-btn"
              onClick={() => setCurrentView('main')}
            >
              <FaArrowLeft />
            </button>
            <h1 className="ge-share-title">Share Earnings</h1>
            <div className="ge-header-accent"></div>
          </header>
          
          <div className="ge-share-content">
            <div className="ge-share-container">
              <div className="ge-intro-section">
                <div className="ge-intro-icon-wrapper">
                  <FaGem className="ge-intro-icon" />
                  <div className="ge-icon-pulse"></div>
                </div>
                <h2 className="ge-intro-title">Showcase Your Success</h2>
                <p className="ge-intro-subtitle">
                  Let the world see your investment achievements
                </p>
              </div>
              
              <form onSubmit={handleSubmit} className="ge-earnings-form">
                <div className="ge-field-group">
                  <label className="ge-field-label">
                    <FaRegLightbulb className="ge-label-icon" />
                    <span>Your Success Story</span>
                  </label>
                  <div className="ge-textarea-wrapper">
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Share your investment journey and what this payout means to you..."
                      className="ge-story-textarea"
                      required
                    />
                    <div className="ge-textarea-accent"></div>
                  </div>
                </div>
                
                <div className="ge-field-group">
                  <label className="ge-field-label">
                    <FaUserCircle className="ge-label-icon" />
                    <span>Contact Number</span>
                  </label>
                  <div className="ge-input-wrapper">
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="Enter your phone number"
                      className="ge-phone-input"
                      required
                    />
                    <div className="ge-input-accent"></div>
                  </div>
                </div>
                
                <div className="ge-field-group">
                  <label className="ge-field-label">
                    <IoWalletOutline className="ge-label-icon" />
                    <span>Earnings Screenshot</span>
                  </label>
                  
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="ge-file-input"
                    id="earnings-upload"
                    required
                  />
                  
                  {selectedImage ? (
                    <div className="ge-preview-container">
                      <img 
                        src={URL.createObjectURL(selectedImage)} 
                        alt="Preview" 
                        className="ge-preview-image"
                      />
                      <button 
                        type="button" 
                        className="ge-remove-btn"
                        onClick={() => setSelectedImage(null)}
                      >
                        <FaTimes />
                      </button>
                      <div className="ge-preview-overlay">
                        <FaCheck className="ge-check-icon" />
                      </div>
                    </div>
                  ) : (
                    <label htmlFor="earnings-upload" className="ge-upload-zone">
                      <div className="ge-upload-animation">
                        <FaCamera className="ge-camera-icon" />
                        <div className="ge-upload-rings">
                          <div className="ge-ring ge-ring-1"></div>
                          <div className="ge-ring ge-ring-2"></div>
                        </div>
                      </div>
                      <div className="ge-upload-content">
                        <h4 className="ge-upload-title">Upload Screenshot</h4>
                        <p className="ge-upload-hint">PNG, JPG or JPEG (max 5MB)</p>
                      </div>
                    </label>
                  )}
                </div>
                
                <button 
                  type="submit" 
                  className={`ge-submit-earnings ${loading ? 'ge-submitting' : ''}`}
                  disabled={loading}
                >
                  <div className="ge-btn-background"></div>
                  <div className="ge-btn-content">
                    {loading ? (
                      <>
                        <FaSpinner className="ge-submit-spinner" />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <FaThumbsUp className="ge-submit-icon" />
                        <span>Share My Earnings</span>
                      </>
                    )}
                  </div>
                </button>
              </form>
            </div>
          </div>
        </>
      )}

      <button 
        className="ge-floating-action"
        onClick={() => setCurrentView('share')}
        aria-label="Share earnings"
      >
        <div className="ge-fab-glow"></div>
        <IoShareSocialOutline className="ge-fab-icon" />
      </button>
      
      <BottomNavigation />
      
      {toastNotification && (
        <div className={`ge-notification ${toastNotification.type === 'success' ? 'ge-success' : 'ge-error'}`}>
          <div className="ge-notification-icon">
            {toastNotification.type === 'success' ? (
              <FaCheck />
            ) : (
              <FaTimes />
            )}
          </div>
          <div className="ge-notification-content">
            <div className="ge-notification-title">
              {toastNotification.type === 'success' ? 'Success!' : 'Error'}
            </div>
            <div className="ge-notification-message">{toastNotification.message}</div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        .ge-earnings-container {
          background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
          min-height: 100vh;
          color: #ffffff;
          font-family: 'Inter', system-ui, sans-serif;
          padding-bottom: 100px;
          position: relative;
          overflow-x: hidden;
        }
        
        .ge-earnings-container::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 300px;
          background: radial-gradient(ellipse at top, rgba(231, 76, 60, 0.15), transparent 70%);
          pointer-events: none;
          z-index: 0;
        }
        
        .ge-earnings-header {
          background: rgba(23, 42, 69, 0.95);
          backdrop-filter: blur(20px);
          padding: 24px 16px;
          position: relative;
          overflow: hidden;
          border-bottom: 1px solid rgba(231, 76, 60, 0.2);
        }
        
        .ge-header-glow {
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(231, 76, 60, 0.1), transparent 50%);
          animation: ge-glow-rotate 20s linear infinite;
        }
        
        @keyframes ge-glow-rotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .ge-header-content {
          max-width: 600px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: relative;
          z-index: 2;
        }
        
        .ge-title-section {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        
        .ge-title-icon-wrapper {
          position: relative;
          width: 56px;
          height: 56px;
          background: linear-gradient(135deg, #e74c3c, #c0392b);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 24px rgba(231, 76, 60, 0.4);
        }
        
        .ge-title-icon {
          font-size: 28px;
          color: white;
          z-index: 2;
        }
        
        .ge-sparkle {
          position: absolute;
          font-size: 12px;
          color: #ffd700;
          animation: ge-sparkle-twinkle 2s infinite;
        }
        
        .ge-sparkle-1 {
          top: -4px;
          right: -4px;
          animation-delay: 0.5s;
        }
        
        .ge-sparkle-2 {
          bottom: -4px;
          left: -4px;
          animation-delay: 1s;
        }
        
        @keyframes ge-sparkle-twinkle {
          0%, 100% { opacity: 0; transform: scale(0.5); }
          50% { opacity: 1; transform: scale(1); }
        }
        
        .ge-title-text {
          display: flex;
          flex-direction: column;
        }
        
        .ge-main-title {
          font-size: 24px;
          font-weight: 800;
          margin: 0;
          background: linear-gradient(135deg, #ffffff, #e74c3c);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .ge-subtitle {
          font-size: 14px;
          color: #8892b0;
          margin: 4px 0 0 0;
        }
        
        .ge-stats-mini {
          background: rgba(231, 76, 60, 0.1);
          border: 1px solid rgba(231, 76, 60, 0.3);
          border-radius: 12px;
          padding: 12px;
          backdrop-filter: blur(10px);
        }
        
        .ge-stat-item {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #e74c3c;
          font-weight: 700;
        }
        
        .ge-stat-icon {
          font-size: 18px;
        }
        
        .ge-earnings-content {
          max-width: 600px;
          margin: 0 auto;
          padding: 24px 16px;
          position: relative;
          z-index: 1;
        }
        
        .ge-loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 80px 20px;
          text-align: center;
        }
        
        .ge-loading-circle {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: linear-gradient(135deg, #e74c3c, #c0392b);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 24px;
          position: relative;
          animation: ge-loading-pulse 2s infinite;
        }
        
        .ge-loading-inner {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: linear-gradient(135deg, #0f0f23, #1a1a2e);
          position: relative;
        }
        
        .ge-loading-inner::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 30px;
          height: 30px;
          border: 3px solid transparent;
          border-top: 3px solid #e74c3c;
          border-radius: 50%;
          transform: translate(-50%, -50%);
          animation: ge-loading-spin 1s linear infinite;
        }
        
        @keyframes ge-loading-pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(231, 76, 60, 0.4); }
          50% { transform: scale(1.05); box-shadow: 0 0 0 20px rgba(231, 76, 60, 0); }
        }
        
        @keyframes ge-loading-spin {
          0% { transform: translate(-50%, -50%) rotate(0deg); }
          100% { transform: translate(-50%, -50%) rotate(360deg); }
        }
        
        .ge-loading-title {
          font-size: 20px;
          font-weight: 700;
          margin: 0 0 8px 0;
          color: #ffffff;
        }
        
        .ge-loading-text {
          font-size: 14px;
          color: #8892b0;
          margin: 0;
        }
        
        .ge-earnings-grid {
          display: grid;
          gap: 20px;
          grid-template-columns: 1fr;
        }
        
        .ge-earnings-card {
          background: linear-gradient(135deg, rgba(23, 42, 69, 0.9), rgba(16, 20, 31, 0.9));
          border-radius: 20px;
          overflow: hidden;
          border: 1px solid rgba(231, 76, 60, 0.2);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2);
          transition: all 0.4s ease;
          position: relative;
          animation: ge-card-slide-in 0.6s ease-out forwards;
          opacity: 0;
          transform: translateY(30px);
        }
        
        @keyframes ge-card-slide-in {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .ge-earnings-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
          border-color: rgba(231, 76, 60, 0.4);
        }
        
        .ge-card-badge {
          position: absolute;
          top: 16px;
          right: 16px;
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 4px;
          z-index: 3;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }
        
        .ge-badge-icon {
          font-size: 10px;
        }
        
        .ge-image-wrapper {
          position: relative;
          height: 200px;
          overflow: hidden;
          background: rgba(16, 20, 31, 0.8);
        }
        
        .ge-earnings-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
          transition: transform 0.4s ease;
        }
        
        .ge-earnings-card:hover .ge-earnings-image {
          transform: scale(1.05);
        }
        
        .ge-image-overlay {
          position: absolute;
          bottom: 16px;
          left: 16px;
          width: 40px;
          height: 40px;
          background: rgba(231, 76, 60, 0.9);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          backdrop-filter: blur(10px);
        }
        
        .ge-overlay-icon {
          font-size: 18px;
        }
        
        .ge-card-content {
          padding: 20px;
        }
        
        .ge-story-section {
          margin-bottom: 20px;
          position: relative;
        }
        
        .ge-quote-wrapper {
          position: absolute;
          top: -8px;
          left: -8px;
          width: 32px;
          height: 32px;
          background: linear-gradient(135deg, #e74c3c, #c0392b);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .ge-quote-icon {
          font-size: 14px;
          color: white;
        }
        
        .ge-story-description {
          margin: 0;
          padding-left: 32px;
          color: #e2e8f0;
          font-size: 14px;
          line-height: 1.6;
          font-style: italic;
        }
        
        .ge-user-section {
          display: flex;
          align-items: center;
          gap: 12px;
          padding-top: 16px;
          border-top: 1px solid rgba(231, 76, 60, 0.1);
        }
        
        .ge-user-avatar {
          width: 44px;
          height: 44px;
          background: linear-gradient(135deg, #e74c3c, #c0392b);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        
        .ge-avatar-icon {
          font-size: 24px;
          color: white;
        }
        
        .ge-user-details {
          flex: 1;
        }
        
        .ge-user-phone {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: #ffffff;
          margin-bottom: 4px;
        }
        
        .ge-date-wrapper {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        .ge-clock-icon {
          font-size: 12px;
          color: #e74c3c;
        }
        
        .ge-story-date {
          font-size: 12px;
          color: #8892b0;
        }
        
        .ge-empty-earnings {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 60px 20px;
          position: relative;
        }
        
        .ge-empty-animation {
          position: relative;
          margin-bottom: 32px;
        }
        
        .ge-empty-circle {
          width: 120px;
          height: 120px;
          background: linear-gradient(135deg, #e74c3c, #c0392b);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
          animation: ge-empty-float 3s ease-in-out infinite;
        }
        
        .ge-empty-icon {
          font-size: 48px;
          color: white;
        }
        
        .ge-floating-icons {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
        }
        
        .ge-float-icon {
          position: absolute;
          font-size: 20px;
          color: #e74c3c;
          animation: ge-icon-float 4s ease-in-out infinite;
        }
        
        .ge-float-1 {
          top: 20px;
          left: 20px;
          animation-delay: 0s;
        }
        
        .ge-float-2 {
          top: 20px;
          right: 20px;
          animation-delay: 1.3s;
        }
        
        .ge-float-3 {
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          animation-delay: 2.6s;
        }
        
        @keyframes ge-empty-float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes ge-icon-float {
          0%, 100% { opacity: 0.5; transform: translateY(0px) scale(1); }
          50% { opacity: 1; transform: translateY(-15px) scale(1.2); }
        }
        
        .ge-empty-title {
          font-size: 24px;
          font-weight: 700;
          color: #ffffff;
          margin: 0 0 12px 0;
        }
        
        .ge-empty-description {
          font-size: 16px;
          color: #8892b0;
          margin: 0 0 32px 0;
          line-height: 1.6;
          max-width: 320px;
        }
        
        .ge-share-earnings-btn {
          background: linear-gradient(135deg, #e74c3c, #c0392b);
          color: white;
          border: none;
          border-radius: 16px;
          padding: 16px 24px;
          font-size: 16px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 8px 24px rgba(231, 76, 60, 0.3);
        }
        
        .ge-share-earnings-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 32px rgba(231, 76, 60, 0.4);
        }
        
        .ge-btn-icon {
          font-size: 18px;
        }
        
       .ge-floating-action {
        position: fixed;
        top: 90px; /* Position below the header */
        right: 20px;
        width: 60px;
        height: 60px;
        background: linear-gradient(135deg, #e74c3c, #c0392b);
        border: none;
        border-radius: 50%;
        color: white;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 8px 24px rgba(231, 76, 60, 0.4);
        transition: all 0.3s ease;
        z-index: 1000;
        overflow: hidden;
      }
        
        .ge-fab-glow {
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.2), transparent 70%);
          animation: ge-fab-glow-rotate 3s linear infinite;
        }
        
        @keyframes ge-fab-glow-rotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .ge-floating-action:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(231, 76, 60, 0.6);
        }
        
        .ge-fab-icon {
          font-size: 24px;
          position: relative;
          z-index: 2;
        }
        
        .ge-share-header {
          background: rgba(23, 42, 69, 0.95);
          backdrop-filter: blur(20px);
          padding: 16px;
          display: flex;
          align-items: center;
          gap: 16px;
          border-bottom: 1px solid rgba(231, 76, 60, 0.2);
          position: relative;
          overflow: hidden;
        }
        
        .ge-header-accent {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, #e74c3c, transparent);
        }
        
        .ge-back-btn {
          width: 44px;
          height: 44px;
          background: rgba(231, 76, 60, 0.1);
          border: 1px solid rgba(231, 76, 60, 0.3);
          border-radius: 12px;
          color: #e74c3c;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }
        
        .ge-back-btn:hover {
          background: rgba(231, 76, 60, 0.2);
          transform: translateX(-2px);
        }
        
        .ge-share-title {
          font-size: 20px;
          font-weight: 700;
          color: #ffffff;
          margin: 0;
        }
        
        .ge-share-content {
          max-width: 600px;
          margin: 0 auto;
          padding: 24px 16px;
        }
        
        .ge-share-container {
          background: linear-gradient(135deg, rgba(23, 42, 69, 0.8), rgba(16, 20, 31, 0.8));
          border-radius: 24px;
          padding: 32px;
          border: 1px solid rgba(231, 76, 60, 0.2);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
          backdrop-filter: blur(20px);
        }
        
        .ge-intro-section {
          text-align: center;
          margin-bottom: 40px;
        }
        
        .ge-intro-icon-wrapper {
          position: relative;
          display: inline-block;
          margin-bottom: 20px;
        }
        
        .ge-intro-icon {
          font-size: 48px;
          color: #e74c3c;
          position: relative;
          z-index: 2;
        }
        
        .ge-icon-pulse {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 80px;
          height: 80px;
          border: 2px solid rgba(231, 76, 60, 0.3);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          animation: ge-pulse-ring 2s infinite;
        }
        
        @keyframes ge-pulse-ring {
          0% { transform: translate(-50%, -50%) scale(0.8); opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(1.2); opacity: 0; }
        }
        
        .ge-intro-title {
          font-size: 28px;
          font-weight: 800;
          color: #ffffff;
          margin: 0 0 8px 0;
          background: linear-gradient(135deg, #ffffff, #e74c3c);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .ge-intro-subtitle {
          font-size: 16px;
          color: #8892b0;
          margin: 0;
        }
        
        .ge-earnings-form {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }
        
        .ge-field-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .ge-field-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 600;
          color: #e2e8f0;
        }
        
        .ge-label-icon {
          font-size: 16px;
          color: #e74c3c;
        }
        
        .ge-textarea-wrapper {
          position: relative;
        }
        
        .ge-story-textarea {
          width: 100%;
          min-height: 120px;
          background: rgba(16, 20, 31, 0.6);
          border: 1px solid rgba(231, 76, 60, 0.2);
          border-radius: 16px;
          padding: 16px;
          color: #ffffff;
          font-size: 14px;
          font-family: inherit;
          resize: vertical;
          transition: all 0.3s ease;
          box-sizing: border-box;
        }
        
        .ge-story-textarea:focus {
          outline: none;
          border-color: #e74c3c;
          box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.1);
        }
        
        .ge-story-textarea::placeholder {
          color: #8892b0;
        }
        
        .ge-textarea-accent {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, #e74c3c, transparent);
          border-radius: 0 0 16px 16px;
          transform: scaleX(0);
          transition: transform 0.3s ease;
        }
        
        .ge-story-textarea:focus + .ge-textarea-accent {
          transform: scaleX(1);
        }
        
        .ge-input-wrapper {
          position: relative;
        }
        
        .ge-phone-input {
          width: 100%;
          background: rgba(16, 20, 31, 0.6);
          border: 1px solid rgba(231, 76, 60, 0.2);
          border-radius: 16px;
          padding: 16px;
          color: #ffffff;
          font-size: 14px;
          font-family: inherit;
          transition: all 0.3s ease;
          box-sizing: border-box;
        }
        
        .ge-phone-input:focus {
          outline: none;
          border-color: #e74c3c;
          box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.1);
        }
        
        .ge-phone-input::placeholder {
          color: #8892b0;
        }
        
        .ge-input-accent {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, #e74c3c, transparent);
          border-radius: 0 0 16px 16px;
          transform: scaleX(0);
          transition: transform 0.3s ease;
        }
        
        .ge-phone-input:focus + .ge-input-accent {
          transform: scaleX(1);
        }
        
        .ge-file-input {
          display: none;
        }
        
        .ge-upload-zone {
          background: rgba(16, 20, 31, 0.4);
          border: 2px dashed rgba(231, 76, 60, 0.3);
          border-radius: 20px;
          padding: 32px;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
        }
        
        .ge-upload-zone:hover {
          border-color: #e74c3c;
          background: rgba(231, 76, 60, 0.05);
        }
        
        .ge-upload-animation {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .ge-camera-icon {
          font-size: 40px;
          color: #e74c3c;
          position: relative;
          z-index: 2;
        }
        
        .ge-upload-rings {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
        
        .ge-ring {
          position: absolute;
          border: 2px solid rgba(231, 76, 60, 0.2);
          border-radius: 50%;
          animation: ge-ring-pulse 2s infinite;
        }
        
        .ge-ring-1 {
          width: 80px;
          height: 80px;
          top: -40px;
          left: -40px;
          animation-delay: 0s;
        }
        
        .ge-ring-2 {
          width: 100px;
          height: 100px;
          top: -50px;
          left: -50px;
          animation-delay: 0.5s;
        }
        
        @keyframes ge-ring-pulse {
          0% { transform: scale(0.8); opacity: 1; }
          100% { transform: scale(1.2); opacity: 0; }
        }
        
        .ge-upload-content {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        
        .ge-upload-title {
          font-size: 18px;
          font-weight: 600;
          color: #ffffff;
          margin: 0;
        }
        
        .ge-upload-hint {
          font-size: 14px;
          color: #8892b0;
          margin: 0;
        }
        
        .ge-preview-container {
          position: relative;
          border-radius: 16px;
          overflow: hidden;
          background: rgba(16, 20, 31, 0.6);
          border: 1px solid rgba(231, 76, 60, 0.2);
        }
        
        .ge-preview-image {
          width: 100%;
          height: 200px;
          object-fit: contain;
          display: block;
        }
        
        .ge-remove-btn {
          position: absolute;
          top: 12px;
          right: 12px;
          width: 32px;
          height: 32px;
          background: rgba(220, 53, 69, 0.9);
          border: none;
          border-radius: 50%;
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
        }
        
        .ge-remove-btn:hover {
          background: #dc3545;
          transform: scale(1.1);
        }
        
        .ge-preview-overlay {
          position: absolute;
          bottom: 12px;
          left: 12px;
          width: 32px;
          height: 32px;
          background: rgba(16, 185, 129, 0.9);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          backdrop-filter: blur(10px);
        }
        
        .ge-check-icon {
          font-size: 14px;
        }
        
        .ge-submit-earnings {
          background: linear-gradient(135deg, #e74c3c, #c0392b);
          border: none;
          border-radius: 16px;
          padding: 0;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          box-shadow: 0 8px 24px rgba(231, 76, 60, 0.3);
        }
        
        .ge-submit-earnings:hover:not(.ge-submitting) {
          transform: translateY(-2px);
          box-shadow: 0 12px 32px rgba(231, 76, 60, 0.4);
        }
        
        .ge-submit-earnings.ge-submitting {
          opacity: 0.8;
          cursor: not-allowed;
        }
        
        .ge-btn-background {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
          transform: translateX(-100%);
          transition: transform 0.6s ease;
        }
        
        .ge-submit-earnings:hover .ge-btn-background {
          transform: translateX(100%);
        }
        
        .ge-btn-content {
          position: relative;
          z-index: 2;
          padding: 18px 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 16px;
          font-weight: 600;
          color: white;
        }
        
        .ge-submit-icon {
          font-size: 18px;
        }
        
        .ge-submit-spinner {
          font-size: 18px;
          animation: ge-spinner-rotate 1s linear infinite;
        }
        
        @keyframes ge-spinner-rotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .ge-notification {
          position: fixed;
          top: 24px;
          right: 24px;
          background: rgba(23, 42, 69, 0.95);
          border-radius: 16px;
          padding: 16px;
          display: flex;
          align-items: center;
          gap: 12px;
          max-width: 320px;
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3);
          backdrop-filter: blur(20px);
          z-index: 10000;
          animation: ge-notification-slide-in 0.3s ease-out;
        }
        
        @keyframes ge-notification-slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        .ge-notification.ge-success {
          border-left: 4px solid #10b981;
        }
        
        .ge-notification.ge-error {
          border-left: 4px solid #ef4444;
        }
        
        .ge-notification-icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        
        .ge-success .ge-notification-icon {
          background: rgba(16, 185, 129, 0.2);
          color: #10b981;
        }
        
        .ge-error .ge-notification-icon {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }
        
        .ge-notification-content {
          flex: 1;
        }
        
        .ge-notification-title {
          font-size: 14px;
          font-weight: 600;
          color: #ffffff;
          margin: 0 0 4px 0;
        }
        
        .ge-notification-message {
          font-size: 12px;
          color: #8892b0;
          margin: 0;
          line-height: 1.4;
        }
        
        @media (max-width: 480px) {
          .ge-earnings-header {
            padding: 20px 12px;
          }
          
          .ge-header-content {
            flex-direction: column;
            gap: 16px;
            align-items: flex-start;
          }
          
          .ge-main-title {
            font-size: 20px;
          }
          
          .ge-earnings-content {
            padding: 20px 12px;
          }
          
          .ge-share-content {
            padding: 20px 12px;
          }
          
          .ge-share-container {
            padding: 24px 20px;
          }
          
          .ge-intro-title {
            font-size: 24px;
          }
          
          .ge-floating-action {
            top: 16px;
            right: 16px;
          }
          
          .ge-notification {
            top: 16px;
            right: 16px;
            left: 16px;
            max-width: none;
          }
        }
      `}
       </style>
    </div>
  );
};

export default SuccessStories;